import React, { useEffect, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import ImageUpload from '@components/ImageUpload';
import { toastError, toastSuccess } from '@components/Toast';

import { fetchEmbellishmentSubCategory, fetchEnumFilter } from '@store/design/action';
import { fetchEmbellishmentDetails } from '@store/material/action';
import { getEnumFilter, getMaterialsDetails } from '@store/reducers';
import { fetchAllTags } from '@store/supplier/action';

import ApiClient from '@lib/apiClient';

import { MATERIAL_TYPE } from '@/constants/constant';

const Embellishment = ({ materialData, onclose, viewType, renderFrom, saveEventLog }) => {
	const [show, setShow] = useState(true);
	const [selectedItem, setSelecteItem] = useState(null);
	const [imageData, setImageData] = useState(null);
	const [isLoading, setIsLoading] = useState(false);
	const [, setInitialLoading] = useState(false);
	const [materialId, setMaterialId] = useState('');
	const [errors, setErrors] = useState({});
	const dispatch = useDispatch();
	const tagType = 'MATERIAL_SUSTAINABLE_TAG';
	const { id } = useParams();

	let allCategories = useSelector(getEnumFilter);
	let materialDetails = useSelector(getMaterialsDetails);

	useEffect(() => {
		setInitialLoading(true);
		dispatch(fetchAllTags('', tagType));
		dispatch(fetchEnumFilter('info.nitex.app.materials.EmbellishmentCategory')).finally(() => {
			setInitialLoading(false);
		});
		dispatch(fetchEmbellishmentSubCategory()).finally(() => {
			setInitialLoading(false);
		});
		if (viewType === 'EDIT' && materialData) {
			dispatch(fetchEmbellishmentDetails(materialData?.materialId)).finally(() => {
				setInitialLoading(false);
			});
		}
	}, [materialData]);

	useEffect(() => {
		if (viewType === 'EDIT' && materialData) {
			const itemDetails = materialDetails?.data;
			setMaterialId(itemDetails?.id);
			setImageData({ documentDTO: { documentPath: itemDetails?.documentPath } });
			setSelecteItem({
				...selectedItem,
				name: itemDetails?.name,
				category: itemDetails?.category,
				subCategory: itemDetails?.subCategory,
				description: itemDetails?.description,
				documentId: itemDetails?.documentId || null,
				materialType: 'EMBELLISHMENT',
				tagRequestList: itemDetails?.tagResponseList?.map((item) => {
					return {
						id: item.id,
						text: item.text,
						value: item.id,
						label: item.text
					};
				})
			});
		}
	}, [materialDetails]);

	const handleClose = (value) => {
		setShow(false);
		onclose(value);
	};

	const onAdd = async (data) => {
		setIsLoading(true);
		await ApiClient.POST('uploadDocument', data)
			.then(({ data }) => {
				setIsLoading(false);
				if (data) {
					setImageData({ documentDTO: data });
					setSelecteItem({ ...selectedItem, documentId: data.id, documentIdList: [data.id] });
				}
			})
			.catch(({ response }) => {
				setIsLoading(false);
				if (response && response.data && response.data.message) {
					toastError(response.data.message);
				} else {
					toastError('Request was unsuccessful.');
				}
			});
	};

	const onUploadImage = (image) => {
		setImageData({ documentDTO: image });
		onAdd(image);
	};

	const isValidForm = (body) => {
		if (body.name?.trim() === '' || !body.name) {
			setErrors({ ...errors, nameError: 'Product name is required' });
			return false;
		}
		if (!body.category || body.category === '') {
			setErrors({ ...errors, categoryTypeError: 'Category is required' });
			return false;
		}
		return true;
	};

	const addNewEmbellishment = async (body) => {
		const isValid = isValidForm(body);
		if (!isValid) return;
		setIsLoading(true);
		await ApiClient.POST('addNewEmbellishment', { ...body, materialType: MATERIAL_TYPE.EMBELLISHMENT })
			.then(({ data }) => {
				setIsLoading(false);
				if (data && data.success) {
					if (renderFrom && renderFrom === 'SUPPLIER_ADD') {
						saveEventLog(id, body.name);
					} else if (renderFrom && renderFrom === 'MATERIAL_ADD') {
						saveEventLog(body.name, MATERIAL_TYPE.EMBELLISHMENT);
					}
					toastSuccess(data.message);
					handleClose();
				}
			})
			.catch(({ response }) => {
				setIsLoading(false);
				if (response && response.data && response.data.message) {
					toastError(response.data.message);
				} else {
					toastError('Request was unsuccessful.');
				}
			});
	};

	const updateEmbellishment = async (body) => {
		const isValid = isValidForm(body);
		if (!isValid) return;
		setIsLoading(true);
		await ApiClient.PUT('updateEmbellishment', body, materialId)
			.then(({ data }) => {
				setIsLoading(false);
				if (data && data.success) {
					if (renderFrom && renderFrom === 'MATERIAL_EDIT') {
						saveEventLog(body.name);
					}
					toastSuccess(data.message);
					handleClose('updated');
				}
			})
			.catch(({ response }) => {
				setIsLoading(false);
				if (response && response.data && response.data.message) {
					toastError(response.data.message);
				} else {
					toastError('Request was unsuccessful.');
				}
			});
	};

	const onSubmit = () => {
		let body = { ...selectedItem };

		if (viewType === 'ADD') {
			addNewEmbellishment(body);
		}
		if (viewType === 'EDIT') {
			updateEmbellishment(body);
		}
	};

	return (
		<Modal show={show} onHide={handleClose} backdrop="static" centered className="width-784-popup">
			<div className="common-popup width-784">
				<Modal.Header closeButton>
					<Modal.Title>
						<div className="popup-tilte">
							<h3 className="semibold-16 mb-0">Embellishment material</h3>
						</div>
					</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<div className="common-popup-body">
						<div className="container px-0">
							<div className="row">
								<div className="col-md-3">
									<div className="upload-section">
										{imageData?.documentDTO ? (
											<>
												<img
													className="border-0"
													src={
														imageData.documentDTO.base64Str
															? imageData.documentDTO.base64Str
															: imageData.documentDTO.documentPath ||
																imageData.documentDTO.docUrl
													}
													alt=""
												/>
												<ImageUpload
													size="sm"
													onUploadImage={onUploadImage}
													docType={'MATERIAL_DESIGN'}
													type={'image'}
													isEditable={true}
												/>
											</>
										) : (
											<ImageUpload
												size="sm"
												onUploadImage={onUploadImage}
												docType={'MATERIAL_DESIGN'}
												type={'image'}
											/>
										)}
									</div>
								</div>
								<div className="col-md-9">
									<div className="row">
										<div className="col-md-12">
											<h3 className="semibold-16">Material info</h3>
										</div>
										<div className="col-md-12">
											<div className="form-group ">
												<label htmlFor="pname">Material name*</label>
												<input
													type="text"
													autoComplete="off"
													id="pname"
													value={selectedItem?.name}
													onChange={(e) =>
														setSelecteItem({
															...selectedItem,
															name: e.target.value
														})
													}
												/>
												{errors.nameError && (
													<p className="error">{errors.nameError}</p>
												)}
											</div>
										</div>
										<div className="col-md-6">
											<div className="form-group ">
												<label htmlFor="category">Category*</label>

												<select
													className="w-100"
													name="category"
													id="category"
													value={selectedItem?.category}
													onChange={(e) =>
														setSelecteItem({
															...selectedItem,
															category: e.target.value
														})
													}
												>
													<option value={''}>Select</option>
													{allCategories?.embellishmentCategory?.map((item) => (
														<option value={item.constant} key={item.constant}>
															{item.name}
														</option>
													))}
												</select>
												{errors.categoryTypeError && (
													<p className="error">{errors.categoryTypeError}</p>
												)}
											</div>
										</div>
										<div className="col-md-6">
											<div className="form-group ">
												<label htmlFor="SubCategory">Sub category</label>

												<input
													type="text"
													autoComplete="off"
													className="w-100"
													name="SubCategory"
													id="SubCategory"
													value={selectedItem?.subCategory}
													onChange={(e) =>
														setSelecteItem({
															...selectedItem,
															subCategory: e.target.value
														})
													}
												/>
											</div>
										</div>

										<div className="col-md-12">
											<h3 className="semibold-16">Others info</h3>
										</div>
										<div className="col-md-12">
											<div className="form-group mb-0">
												<label htmlFor="description">Description</label>
												<textarea
													name="description"
													id="description"
													placeholder="Write description"
													defaultValue={''}
													value={selectedItem?.description}
													onChange={(e) =>
														setSelecteItem({
															...selectedItem,
															description: e.target.value
														})
													}
												/>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</Modal.Body>
				<Modal.Footer>
					<div className="footer-buttons text-right">
						<button className="button outline size32" onClick={handleClose}>
							Cancel
						</button>
						<button
							disabled={isLoading}
							className="button size32 ml-2"
							onClick={() => onSubmit()}
						>
							Submit
						</button>
					</div>
				</Modal.Footer>
			</div>
		</Modal>
	);
};

export default Embellishment;
